import { useEffect, useState } from 'react'

/**
 * Star count for the nav's GitHub link, behind the `nav-github-stars`
 * experiment flag (see specs/experiments.feature): `control` keeps the
 * icon-only link, `stars` shows the count. The prerendered HTML ships the
 * control look; the `stars` variant swaps in STARS_FALLBACK immediately and
 * then the real count from the GitHub API (unauthenticated, CORS-enabled),
 * cached in localStorage for an hour so this static MPA does not hit the API
 * on every page load.
 */
export const NAV_GITHUB_STARS_FLAG = 'nav-github-stars'
export const GITHUB_REPO = 'langwatch/langwatch'
export const STARS_FALLBACK = 3359

const CACHE_KEY = 'lw:gh-stars'
const CACHE_TTL_MS = 60 * 60 * 1000

/** GitHub's own display format: 999, 3.4k, 12.3k, 123k. */
export function formatStars(n: number): string {
  if (n < 1000) return String(n)
  if (n < 100_000) {
    const k = (Math.round(n / 100) / 10).toFixed(1).replace(/\.0$/, '')
    return `${k}k`
  }
  return `${Math.round(n / 1000)}k`
}

function readCache(): number | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const { n, t } = JSON.parse(raw) as { n: number; t: number }
    if (typeof n !== 'number' || typeof t !== 'number') return null
    if (Date.now() - t > CACHE_TTL_MS) return null
    return n
  } catch {
    return null
  }
}

function writeCache(n: number) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ n, t: Date.now() }))
  } catch {
    // Private mode or full storage: the fallback still shows.
  }
}

/** Live star count; pass `enabled: false` to skip the fetch (variant hidden). */
export function useGithubStars(enabled = true): number {
  const [stars, setStars] = useState(STARS_FALLBACK)

  useEffect(() => {
    if (!enabled) return
    const cached = readCache()
    if (cached !== null) {
      setStars(cached)
      return
    }
    const ctrl = new AbortController()
    fetch(`https://api.github.com/repos/${GITHUB_REPO}`, {
      signal: ctrl.signal,
      headers: { Accept: 'application/vnd.github+json' },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: { stargazers_count?: number }) => {
        if (typeof data.stargazers_count === 'number') {
          setStars(data.stargazers_count)
          writeCache(data.stargazers_count)
        }
      })
      .catch(() => {
        // Rate-limited or offline: keep the baked fallback.
      })
    return () => ctrl.abort()
  }, [enabled])

  return stars
}
